import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Buffer } from 'buffer';
import {
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
  Backdrop,
  LinearProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TimeSelector from './TimeSelector';
import Followers from './Followers';
import { CreateAppointmentRequest } from '../../types/CreateAppointmentRequest';
import { bookingService } from '../../services/bookingService';
import { LoanDetails } from '../../../src/types/loanDetails';
import { BookingService, TimeSlot } from '../../types/service';
import { useAuth } from '../../context/AuthContext';
import { calendarBooking } from '../../types/calendarBooking';
import { mapCalendarBookingToFormData } from '../../services/bookingFormUtils';
import { toLocalISOString } from '../../utils/general';
import { TimezoneService } from '../../services/timezoneUtils';
import { EmailRequestDTO, FileRequestDTO } from '../../types/email';
import { AvailabilityService, AvailabilitySettings, DateRange } from '../../services/availabilityService';
import EnhancedFollowers from './EnhancedFollowers';
import { ICSGeneratorService } from '../../services/icsGeneratorService';
import AdminOverride from './AdminOverride';

type BookingFormProps = {
  onClose: () => void;
  onSuccess?: (bookingData: CreateAppointmentRequest, response: any, changeInfo?: { isDateTimeChanged: boolean; isFollowerOnlyUpdate: boolean }) => void;
  initialData?: calendarBooking;
  readOnly?: boolean;
  setLoading?: (loading: boolean) => void;
  prefilledLoanId?: string;
}

interface FollowerWithType {
  email: string;
  type: string;
}
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeCustomLocation = (value?: string | null) => (value || '').trim();

const CreateBookingForm: React.FC<BookingFormProps> = ({ 
  onClose, 
  onSuccess, 
  initialData, 
  readOnly = false,
  setLoading,
  prefilledLoanId
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAdmin, user } = useAuth();
  
  // Loading states for different operations
  const [loadingStates, setLoadingStates] = useState({
    loanDetails: false,
    services: false,
    timeSlots: false,
    followers: false,
    submitting: false,
    initializing: false,
    sendingEmail: false
  });

  const [selectedBorrowerEmails, setSelectedBorrowerEmails] = useState<string[]>([]);
  
  const [availabilitySettings, setAvailabilitySettings] = useState<AvailabilitySettings | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  const fetchTimeSlotsGenRef = useRef<number>(0);

  const [isOverrideMode, setIsOverrideMode] = useState(false);
  const [overrideSlot, setOverrideSlot] = useState<TimeSlot | null>(null);
  
  const [services, setServices] = useState<BookingService[]>([]);
  const [fetchedFollowers, setFetchedFollowers] = useState<string[]>([]);
  const [addedFollowers, setAddedFollowers] = useState<string[]>([]);
  const [combinedFollowers, setCombinedFollowers] = useState<string[]>([]);
  const [showLoanDetails, setShowLoanDetails] = useState<boolean>(false);
  const [loanDetails, setLoanDetails] = useState<LoanDetails>();
  const [selectedService, setSelectedService] = useState<BookingService>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [error, setError] = useState<string>("");

  const [loanDetailsFollowers, setLoanDetailsFollowers] = useState<FollowerWithType[]>([]);
  const [selectedLoanFollowers, setSelectedLoanFollowers] = useState<FollowerWithType[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [customLocationTouched, setCustomLocationTouched] = useState(false);
  
  const [isLoanDetailsValidated, setIsLoanDetailsValidated] = useState<boolean>(false);

  const [initialSelectedDate, setInitialSelectedDate] = useState<Date | null>(null);

  const [borrowersInitialized, setBorrowersInitialized] = useState(false);
  
  const [bookingData, setBookingData] = useState<CreateAppointmentRequest>(
  {
    ServiceId: "",
    ServiceName: "",
    CustomLocation: "",
    ServicePrice: 0,
    EncompassDetails: {
      EncompassLoanId: "",
      EncompassLoanNumber: "",
      LoanCloser: "",
      loanCloserEmail: "",
      LoanOfficer: "",
      loanOfficerEmail: "",
      dpa: ""
    },
    BorrowerInformation: {
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNumber: "",
      Address: {
        Street: "",
        City: "",
        State: "",
        ZipCode: "",
      },
    },
    DateTimeInfo: {
      SelectedDate: "",
      SelectedTime: "",
      FromDate: "",
      ToDate: "",
    },
    Followers: "",
    Duration: "PT1H",
    PreBuffer: "PT0S",
    PostBuffer: "PT30M",
    PriceType: "notSet",
    StaffMemberIds: [],
    LoanDetails: {
      notes: "",
      loanType: "",
      loanPurpose: ""
    }
  });

  const isEditMode = !!initialData && !readOnly;
  const isCreateMode = !initialData && !readOnly;
  const isViewMode = readOnly;
  
  // Legacy aliases for existing code
  const editMode = isEditMode;
  const readOnlyMode = isViewMode;

  // Helper function to update loading states
  const updateLoadingState = (key: keyof typeof loadingStates, value: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const handleOverrideSlot = useCallback((slot: TimeSlot) => {
    setOverrideSlot(slot);
    setSelectedSlot(slot);
    setIsOverrideMode(true);
    
    setTimeSlots([]);
  }, []);

  const handleCancelOverride = useCallback(() => {
    setIsOverrideMode(false);
    setOverrideSlot(null);
    setSelectedSlot(null);
    
    if (selectedDate && bookingData.ServiceId && !readOnlyMode) {
      fetchAvailableTimeSlots();
    }
  }, [selectedDate, bookingData.ServiceId, readOnlyMode]);

const parseFollowersWithTypes = (followers: string[]): FollowerWithType[] => {
  return followers.map(follower => {
    if (follower.includes('#')) {
      const [type, email] = follower.split('#');
      
      let groupedType = type;
      const lowerType = type.toLowerCase();
      if (/^seller\d*$/.test(lowerType)) {
            groupedType = 'SELLER';
      } 
      return { type: groupedType, email };
    }
    return { type: 'Other', email: follower };
  });
};

  // Check if any loading is in progress
  const isAnyLoading = Object.values(loadingStates).some(Boolean);

  // Get the title based on mode
  const getFormTitle = () => {
    if (readOnly) return 'View Appointment';
    if (initialData) return 'Edit Booking';
    return 'Schedule a New Booking';
  };

  const formatLoanAmount = (amount: number | undefined): string => {
    if (!amount) return '';
    return amount.toLocaleString();
  };

  const fetchAvailabilitySettings = useCallback(async (serviceId?: string) => {
    if (readOnlyMode || isOverrideMode) return;

    setIsLoadingAvailability(true);

    // Only clear slots and selection in create mode, not edit mode
    if (!editMode) {
      setTimeSlots([]);
      setSelectedSlot(null);
    }

    try {
      const settings = await bookingService.getAvailability(serviceId);
      setAvailabilitySettings(settings);

      const calculatedRange = AvailabilityService.calculateDateRange(settings);
      setDateRange(calculatedRange);

      // Only auto-select minimum date in create mode, not edit mode
      if (!editMode && calculatedRange.minDate) {
        setSelectedDate(calculatedRange.minDate);
      }

    } catch (error) {
      console.error('Error fetching availability settings:', error);
      setAvailabilitySettings(null);
      setDateRange(null);
    } finally {
      setIsLoadingAvailability(false);
    }
  }, [editMode, readOnlyMode, isOverrideMode]);

  const handleLoanIdChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    if (readOnlyMode) return;
    
    const loanId = event.target.value;
    setBookingData((prev) => ({
      ...prev,
      EncompassDetails: {
        ...prev.EncompassDetails,
        EncompassLoanId: loanId,
      },
    }));
    
    if (!editMode) {
      setIsLoanDetailsValidated(false);
      setShowLoanDetails(false);
    }
  };

  const handleServiceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    if (readOnlyMode) return;
    
    const selectedValue = event.target.value as string;
    const selectedService = services.find(s => s.displayName === selectedValue);
    
    if (selectedService) {
      
      setTimeSlots([]);
      setSelectedSlot(null);
      setSelectedDate(null);
      
      setSelectedService(selectedService);
      setBookingData(prev => ({
        ...prev,
        ServiceName: selectedService.displayName,
        ServiceId: selectedService.id,
        ServicePrice: selectedService.defaultPrice,
      }));

      if (!readOnlyMode && !isOverrideMode) {
        fetchAvailabilitySettings(selectedService.id);
      }
    }
  };

const sendEmailNotifications = async (response: any) => {
  if (readOnlyMode) return;

  updateLoadingState('sendingEmail', true);
  
  try {
    const currentUserEmail = user?.email || '';
    const globalFollowers = await bookingService.getGlobalFollowers();
    
    // Email notification logic:
    // 1. For new appointments: Send "New Appointment Scheduled" to all followers
    // 2. For date/time changes: Send "Appointment Rescheduled" to all current followers
    // 3. For follower-only changes: Send "Appointment Updated" only to new followers
    // 4. If no new followers are added, skip email notification entirely
    
    let isDateTimeChanged = false;
    let isCustomLocationChanged = false;
    let newFollowers: string[] = [];
    
    if (editMode && initialData) {
      // Check if date/time changed
      const originalDateTime = initialData.start?.dateTime;
      const newDateTime = bookingData.DateTimeInfo.FromDate;
      
      isDateTimeChanged = (originalDateTime !== newDateTime);
      isCustomLocationChanged = normalizeCustomLocation(initialData.customLocation) !== normalizeCustomLocation(bookingData.CustomLocation);
      console.log('🔍 Date/time change detection:', {
        originalDateTime,
        newDateTime,
        isDateTimeChanged
      });
      console.log('📍 Custom location change detection:', {
        originalCustomLocation: initialData.customLocation || '',
        newCustomLocation: bookingData.CustomLocation || '',
        isCustomLocationChanged
      });
      
      // Get original followers
      const originalFollowers = initialData.followers ? 
        initialData.followers.split(',').map(email => email.trim()).filter(email => email.length > 0) : [];
      
      // Get current followers
      const currentFollowers = bookingData.Followers ? 
        bookingData.Followers.split(',').map(email => email.trim()).filter(email => email.length > 0) : [];
      
      // Find new followers (ones not in original list)
      newFollowers = currentFollowers.filter(email => !originalFollowers.includes(email));
      
      console.log('👥 Follower change detection:', {
        originalFollowers,
        currentFollowers,
        newFollowers
      });
    }
    
    let recipientEmails: string[] = [];
    let ccEmails: string[] = [...globalFollowers];
    
    if (currentUserEmail) {
      recipientEmails.push(currentUserEmail);
    }
    
    if (loanDetails?.loanCloserEmail && loanDetails.loanCloserEmail.trim() !== '') {
      recipientEmails.push(loanDetails.loanCloserEmail);
    }
    
    if (loanDetails?.loanOfficerEmail && loanDetails.loanOfficerEmail.trim() !== '') {
      recipientEmails.push(loanDetails.loanOfficerEmail);
    }
    
    if (bookingData.BorrowerInformation.Email && bookingData.BorrowerInformation.Email.trim() !== '') {
      recipientEmails.push(bookingData.BorrowerInformation.Email);
    }
    
    // Determine which followers to notify based on change type
    let followersToNotify: string[] = [];
    if (editMode) {
      if (isDateTimeChanged || isCustomLocationChanged) {
        // If appointment details changed, notify all current followers
        followersToNotify = bookingData.Followers ? 
          bookingData.Followers.split(',').map(email => email.trim()).filter(email => email.length > 0 && email.includes('@') && email.includes('.')) : [];
        console.log('📅 Appointment details changed - notifying all followers:', followersToNotify);
      } else {
        // clear recipient emails
        recipientEmails = [];
        ccEmails = [];
        // If only followers changed, notify only new followers
        followersToNotify = newFollowers.filter(email => email.includes('@') && email.includes('.'));
        console.log('👥 Only followers changed - notifying new followers:', followersToNotify);
        
        // If no new followers, skip email notification
        if (followersToNotify.length === 0) {
          console.log('ℹ️ No new followers added, skipping email notification');
          updateLoadingState('sendingEmail', false);
          return;
        }
      }
    } else {
      // For new appointments, notify all followers
      followersToNotify = bookingData.Followers ? 
        bookingData.Followers.split(',').map(email => email.trim()).filter(email => email.length > 0 && email.includes('@') && email.includes('.')) : [];
      console.log('🆕 New appointment - notifying all followers:', followersToNotify);
    }
    
    recipientEmails.push(...followersToNotify);
    
    const uniqueEmails = Array.from(new Set(recipientEmails))
      .filter(email => email && email.trim() !== '' && email.includes('@') && email.includes('.'));
    
    if (uniqueEmails.length === 0) {
      console.warn('⚠️ No valid recipient emails found, skipping email notification');
      updateLoadingState('sendingEmail', false);
      return;
    }
    
    let appointmentDate: string;
    try {
      const dateParts = bookingData.DateTimeInfo.SelectedDate.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1;
        const day = parseInt(dateParts[2]);
        
        const localDate = new Date(year, month, day);
        appointmentDate = localDate.toLocaleDateString();
      } else {
        appointmentDate = bookingData.DateTimeInfo.SelectedDate;
      }
    } catch (error) {
      console.error('Error parsing appointment date:', error);
      appointmentDate = bookingData.DateTimeInfo.SelectedDate;
    }
    
    const appointmentTime = bookingData.DateTimeInfo.SelectedTime;
    const formattedTime = new Date(`2000-01-01T${appointmentTime}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const borrowerName = `${bookingData.BorrowerInformation.FirstName} ${bookingData.BorrowerInformation.LastName}`.trim();

    // Different titles based on operation type and change type
    let title: string;
    let headerTitle: string;
    let actionText: string;
    
    if (editMode) {
      if (isDateTimeChanged) {
        title = "Appointment Rescheduled";
        headerTitle = "📅 Appointment Rescheduled";
        actionText = "has been rescheduled successfully";
      } else {
        title = "Appointment Updated";
        headerTitle = "📝 Appointment Updated";
        actionText = "has been updated successfully";
      }
    } else {
      title = "New Appointment Scheduled";
      headerTitle = "📊 New Appointment Scheduled";
      actionText = "has been scheduled successfully";
    }
    
    const overrideNotice = isOverrideMode ? 
      `<div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 20px 0;">
        <h4 style="margin-top: 0; color: #856404;">⚠️ Admin Override Notice</h4>
        <p style="margin: 0;">This appointment was created using admin override privileges, bypassing standard booking rules and availability constraints.</p>
      </div>` : '';
    
    const htmlBody = `
      <html>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #2c5aa0; border-bottom: 2px solid #2c5aa0; padding-bottom: 10px;">
              ${headerTitle}
            </h2>


            <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
              <p><strong>Greetings,</strong></p>
              <p>
                ${editMode ? 
                  (isDateTimeChanged ? 
                    'We are pleased to confirm your rescheduled closing appointment with <strong>FEMBi Mortgage</strong>. This email serves as your official appointment reschedule confirmation.' :
                    (isCustomLocationChanged ?
                      'Your closing appointment with <strong>FEMBi Mortgage</strong> has been updated. This email serves as your notification of the updated appointment details.' :
                      'Your closing appointment with <strong>FEMBi Mortgage</strong> has been updated with additional participants. This email serves as your notification of the update.'
                    )
                  ) : 
                  'We are pleased to confirm your upcoming closing appointment with <strong>FEMBi Mortgage</strong>. This email serves as your official appointment confirmation.'
                }
              </p>
              <p>
                Should you have any questions or require further assistance, please do not hesitate to contact your 
                <strong>FEMBi Mortgage Loan Officer</strong>. We are here to support you throughout this process.
              </p>
              <p>
                Thank you for choosing <strong>FEMBi Mortgage</strong>. We look forward to assisting you at your closing.
              </p>

              <hr style="margin: 30px 0;">

              <p><strong>Saludos,</strong></p>
              <p>
                ${editMode ? 
                  (isDateTimeChanged ? 
                    'Nos complace confirmar su cita reprogramada para el cierre con <strong>FEMBi Mortgage</strong>. Este correo electrónico constituye la confirmación oficial de su cita reprogramada.' :
                    (isCustomLocationChanged ?
                      'Su cita para el cierre con <strong>FEMBi Mortgage</strong> ha sido actualizada. Este correo electrónico le notifica los detalles actualizados de la cita.' :
                      'Su cita para el cierre con <strong>FEMBi Mortgage</strong> ha sido actualizada con participantes adicionales. Este correo electrónico le notifica de la actualización.'
                    )
                  ) : 
                  'Nos complace confirmar su cita próxima para el cierre con <strong>FEMBi Mortgage</strong>. Este correo electrónico constituye la confirmación oficial de su cita.'
                }
              </p>
              <p>
                Si tiene alguna pregunta o necesita asistencia adicional, no dude en comunicarse con su 
                <strong>Oficial de Préstamos de FEMBi Mortgage</strong>. Estamos a su disposición para asistirle durante este proceso.
              </p>
              <p>
                Gracias por confiar en <strong>FEMBi Mortgage</strong>. Esperamos poder asistirle en su cierre.
              </p>
            </div>

            <div style="background-color: #f8f9fa; border-left: 4px solid #28a745; padding: 15px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #28a745;">Appointment Details</h3>
              <p><strong>Location:</strong> ${
                bookingData.CustomLocation?.trim()
                  ? bookingData.CustomLocation
                  : getPhysicalAddressForService(bookingData.ServiceName)
              }</p>
              <p><strong>Settlement Agent:</strong> First National Title Services, Inc.</p>
              <p><strong>Date:</strong> ${appointmentDate}</p>
              <p><strong>Time:</strong> ${formattedTime}</p>
              <p><strong>Loan ID:</strong> ${loanDetails?.loanNumber || 'N/A'}</p>
            </div>
            
            <div style="background-color: #e9ecef; border-left: 4px solid #007bff; padding: 15px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #007bff;">Borrower Information</h3>
              <p><strong>Name:</strong> ${borrowerName}</p>
              <p><strong>Address:</strong> ${bookingData.BorrowerInformation.Address.Street}, ${bookingData.BorrowerInformation.Address.City}, ${bookingData.BorrowerInformation.Address.State} ${bookingData.BorrowerInformation.Address.ZipCode}</p>
            </div>
            
            <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #856404;">Loan Information</h3>
              <p><strong>Loan Closer:</strong> ${loanDetails?.loanCloser || 'N/A'}</p>
              ${loanDetails?.loanCloserEmail ? `<p><strong>Loan Closer Email:</strong> ${loanDetails.loanCloserEmail}</p>` : ''}
              <p><strong>Loan Officer:</strong> ${loanDetails?.loanOfficer || 'N/A'}</p>
              ${loanDetails?.loanOfficerEmail ? `<p><strong>Loan Officer Email:</strong> ${loanDetails.loanOfficerEmail}</p>` : ''}
              <p><strong>Loan Type:</strong> ${loanDetails?.loanType || 'N/A'}</p>
              <p><strong>Loan Purpose:</strong> ${loanDetails?.loanPurpose || 'N/A'}</p>
            </div>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; font-size: 12px; color: #6c757d;">
              <p>📅 <strong>Calendar attachment included</strong> - Add this appointment to your calendar by opening the attached .ics file.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const icsContent = ICSGeneratorService.generateICSFromAppointment(bookingData);
    const icsFilename = ICSGeneratorService.generateICSFilename(bookingData);

    const emailRequest: EmailRequestDTO = {
      To: [], 
      Bcc: uniqueEmails,
      Cc: ccEmails.filter(email => email && email.trim() !== '' && email.includes('@')),
      Subject: `${title} - ${bookingData.ServiceName} for ${borrowerName}`,
      Body: htmlBody,
      IsHtml: true,
      Attachments: [
        {
          FileName: icsFilename,
          Extension: "ics",
          Data: Buffer.from(icsContent).toString('base64')
        }
      ]
    };

   
    const emailResponse = await bookingService.sendEmail(emailRequest);
    
  } catch (error) {
    console.error('❌ Error sending email notifications:', error);
    // Don't throw the error - just log it so the booking process isn't interrupted
  } finally {
    updateLoadingState('sendingEmail', false);
  }
};
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (readOnlyMode || loadingStates.submitting) return;

    if (!editMode && !isLoanDetailsValidated && !isOverrideMode) {
      setError("Please enter a valid Loan ID and press Enter to load loan details before submitting.");
      return;
    }

    if (!bookingData.ServiceId) {
      setError("Please select a service.");
      return;
    }

    // In edit mode, allow submission if we have existing time info (for follower-only updates)
    // In create mode, always require a time slot selection
    if (!editMode && !selectedSlot) {
      setError("Please select a time slot or use admin override.");
      return;
    }

    if (editMode && !selectedSlot && !bookingData.DateTimeInfo?.FromDate) {
      setError("Please select a time slot or use admin override.");
      return;
    }

    setError("");
    
    updateLoadingState('submitting', true);
    setLoading?.(true);

    try {
      let response;

      // Detect if admin override was used
      const isUsingAdminOverride = bookingData.StaffMemberIds?.some(
        id => id === 'admin-override'
      );

      if (editMode) {
        const shouldSendCustomLocation =
          customLocationTouched || typeof initialData?.customLocation !== 'undefined';

        const updateData = {
          id: initialData?.bookingId || '',
          selectedDate: bookingData.DateTimeInfo.SelectedDate,
          selectedTime: bookingData.DateTimeInfo.SelectedTime,
          fromDate: bookingData.DateTimeInfo.FromDate,
          toDate: bookingData.DateTimeInfo.ToDate,
          staffMemberIds: bookingData.StaffMemberIds,
          Followers: bookingData.Followers,
          ...(shouldSendCustomLocation ? { CustomLocation: bookingData.CustomLocation || '' } : {}),
          notes: notes,
          IsAdminOverride: isUsingAdminOverride
        };

        response = await bookingService.updateBooking(updateData);
      } else {
        const createData = {
          ...bookingData,
          IsAdminOverride: isUsingAdminOverride,
          LoanDetails: {
            notes: notes,
            loanType: loanDetails?.loanType || '',
            loanPurpose: loanDetails?.loanPurpose || ''
          }
        };
        
        response = await bookingService.postBooking(createData);

        if (response && typeof response === 'object') {
          if ('Status' in response && response.Status === 0) {
            const errorMessage = response.Message || 'Failed to create booking. Please try again.';
            setError(errorMessage);
            console.error('Booking creation failed:', response);
            return;
          }
        }
          
          if ('status' in response && response.status === 0) {
            const errorMessage = response.message || response.Message || 'Failed to create booking. Please try again.';
            setError(errorMessage);
            console.error('Booking creation failed:', response);
            return;
          }
      }

      if (response) {
        await sendEmailNotifications(response);
      }

      if (onSuccess) {
        // Determine change type for confirmation dialog
        let isDateTimeChanged = false;
        let isFollowerOnlyUpdate = false;
        let isCustomLocationChanged = false;
        
        if (editMode && initialData) {
          const originalDateTime = initialData.start?.dateTime;
          const newDateTime = bookingData.DateTimeInfo.FromDate;
          isDateTimeChanged = (originalDateTime !== newDateTime);
          isCustomLocationChanged = normalizeCustomLocation(initialData.customLocation) !== normalizeCustomLocation(bookingData.CustomLocation);
          
          if (!isDateTimeChanged && !isCustomLocationChanged) {
            // Check if followers changed
            const originalFollowers = initialData.followers ? 
              initialData.followers.split(',').map(email => email.trim()).filter(email => email.length > 0) : [];
            const currentFollowers = bookingData.Followers ? 
              bookingData.Followers.split(',').map(email => email.trim()).filter(email => email.length > 0) : [];
            const newFollowers = currentFollowers.filter(email => !originalFollowers.includes(email));
            isFollowerOnlyUpdate = newFollowers.length > 0;
          }
        }
        
        onSuccess(bookingData, response, { isDateTimeChanged, isFollowerOnlyUpdate });
      }
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} booking:`, error);
      const apiError = error as {
        response?: {
          data?: {
            message?: string;
            Message?: string;
            error?: { message?: string };
          };
        };
      };
      const serverMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.Message ||
        apiError.response?.data?.error?.message;

      setError(serverMessage || `Failed to ${editMode ? 'update' : 'create'} booking. Please try again.`);
    } finally {
      updateLoadingState('submitting', false);
      setLoading?.(false);
    }
  };

  const fetchLoanDetails = async () => {
    if (loadingStates.submitting || readOnlyMode) return;

    const loanIdToUse = bookingData.EncompassDetails.EncompassLoanId || prefilledLoanId;

    if (!loanIdToUse?.trim()) {
      setError("Please enter a Loan ID.");
      return;
    }

    updateLoadingState('loanDetails', true);
    setError("");
    
    try{
      const loanDetails = await bookingService.getLoanDetails(loanIdToUse, true);

      if(loanDetails.notes === "Loan Id Already Used"){
        setError("Loan Id Already Used!");
        setIsLoanDetailsValidated(false);
        return;
      }else if(loanDetails.notes === "Invalid Loan Id!"){
        setError("Invalid Loan Id!");
        setIsLoanDetailsValidated(false);
        return;
      }
      
      setLoanDetails(loanDetails);
      setNotes(loanDetails.notes || '');
      
      if (loanDetails.followers && Array.isArray(loanDetails.followers) && loanDetails.followers.length > 0) {
        const followersWithTypes = parseFollowersWithTypes(loanDetails.followers);
        setLoanDetailsFollowers(followersWithTypes);
        setSelectedLoanFollowers(followersWithTypes);
      } else {
        setLoanDetailsFollowers([]);
        setSelectedLoanFollowers([]);
      }

      if (loanDetails.borrowers && Array.isArray(loanDetails.borrowers) && !borrowersInitialized) {
        const borrowerEmails: string[] = [];
        
        loanDetails.borrowers.forEach(borrowerGroup => {
          if (borrowerGroup.borrower?.email) {
            borrowerEmails.push(borrowerGroup.borrower.email);
          }
          if (borrowerGroup.coBorrower?.email) {
            borrowerEmails.push(borrowerGroup.coBorrower.email);
          }
        });
        
        setSelectedBorrowerEmails(borrowerEmails);
        setBorrowersInitialized(true); 
      } else if (!loanDetails.borrowers || !Array.isArray(loanDetails.borrowers)) {
        setSelectedBorrowerEmails([]);
      }
      
      setBookingData((prev) => ({
        ...prev,
        EncompassDetails: {
          EncompassLoanId: loanDetails?.loanId,
          EncompassLoanNumber: loanDetails?.loanNumber,
          LoanCloser: loanDetails?.loanCloser,
          loanCloserEmail: loanDetails?.loanCloserEmail,
          LoanOfficer: loanDetails?.loanOfficer,
          loanOfficerEmail: loanDetails?.loanOfficerEmail,
          dpa: loanDetails?.dpa
        },
        BorrowerInformation: {
          FirstName: loanDetails.borrowerFirstName,
          LastName: loanDetails.borrowerLastName,
          Email: loanDetails.borrowerEmail,
          PhoneNumber: loanDetails.borrowerPhone,
          Address: {
            Street: loanDetails.borrowerAddress,
            City: loanDetails.borrowerCity,
            State: loanDetails.borrowerState,
            ZipCode: loanDetails.borrowerZipCode,
          },
        },
      }));

      setShowLoanDetails(true);
      setIsLoanDetailsValidated(true);
    }catch (error) {
      console.error('Error fetching Loan Details:', error);
      setError("Failed to load loan details. Please check the Loan ID and try again.");
      setIsLoanDetailsValidated(false);
    }finally{
      updateLoadingState('loanDetails', false);
    }
  };


  const handleLoanIdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchLoanDetails();
    }
  };
  const handleBorrowerSelectionChange = useCallback((newSelectedEmails: string[]) => {
    setSelectedBorrowerEmails(newSelectedEmails);
  }, []);

  const fetchAvailableServicesData = useCallback(async () => {
    updateLoadingState('services', true);
    try {
      const response: BookingService[] = await bookingService.getAvailableServices();
      setServices(response);
      
      if (editMode && bookingData.ServiceId) {
        const matchedService = response.find(s => s.id === bookingData.ServiceId);
        if (matchedService) {
          setSelectedService(matchedService);
        }
      }
    } catch (error) {
      console.error('Error fetching available services data:', error);
      setServices([]);
    } finally {
      updateLoadingState('services', false);
    }
  }, [editMode]);

 const handleDateTimeSelect = useCallback(() => {
  if (!selectedDate || readOnlyMode) return;

  if (isOverrideMode && overrideSlot) {
    
    const backendStartTime = overrideSlot.startTime;
    const backendEndTime = overrideSlot.endTime;
    
    const userStartTime = TimezoneService.convertBackendTimeToLocalReliable(backendStartTime);
    const userEndTime = TimezoneService.convertBackendTimeToLocalReliable(backendEndTime);
    
    const dateStr = format(userStartTime, 'yyyy-MM-dd');
    const time24 = format(userStartTime, 'HH:mm');

    setBookingData((prev) => ({
      ...prev,
      DateTimeInfo: {
        SelectedDate: dateStr,
        SelectedTime: time24,
        FromDate: backendStartTime,
        ToDate: backendEndTime,
      },
      StaffMemberIds: [overrideSlot.staffMemberId || 'admin-override'],
    }));
    
    return;
  }

  if (!selectedSlot) return;
  
  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const startTimeUser = TimezoneService.convertBackendTimeToLocalReliable(selectedSlot.startTime);
  const time24 = format(startTimeUser, 'HH:mm');

  setBookingData((prev) => ({
    ...prev,
    DateTimeInfo: {
      SelectedDate: dateStr,
      SelectedTime: time24,
      FromDate: selectedSlot.startTime,
      ToDate: selectedSlot.endTime,
    },
    StaffMemberIds: [selectedSlot.staffMemberId],
  }));
}, [selectedSlot, selectedDate, readOnlyMode, isOverrideMode, overrideSlot]);

const fetchAvailableTimeSlots = useCallback(async () => {
    if (!selectedDate || !bookingData?.ServiceId || isOverrideMode) return;

    // Increment the generation counter so any in-flight fetch for an older
    // date can detect it has been superseded and discard its results.
    const generation = ++fetchTimeSlotsGenRef.current;

    updateLoadingState('timeSlots', true);
    setTimeSlots([]);
    setSelectedSlot(null);

    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const backendDateString = `${year}-${month}-${day}T00:00:00`;

      let currentSelectedSlot: { startTime: string; endTime: string } | undefined;

      if (editMode && bookingData.DateTimeInfo?.FromDate && bookingData.DateTimeInfo?.ToDate) {
        currentSelectedSlot = {
          startTime: bookingData.DateTimeInfo.FromDate,
          endTime: bookingData.DateTimeInfo.ToDate
        };
      }

      const response: TimeSlot[] = await bookingService.getAvailableTimeSlots(
        bookingData.ServiceId,
        backendDateString,
        editMode,
        currentSelectedSlot
      );

      // Discard results if another fetch started while this one was in-flight.
      if (generation !== fetchTimeSlotsGenRef.current) return;

      let filteredSlots = response;

      if (!readOnlyMode && dateRange && !isOverrideMode && !editMode) {
        filteredSlots = AvailabilityService.filterTimeSlots(response, selectedDate, dateRange);
      }

      setTimeSlots(filteredSlots);

      if (editMode && bookingData.DateTimeInfo?.SelectedTime && !selectedSlot && initialSelectedDate) {
        const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
        const originalDateStr = format(initialSelectedDate, 'yyyy-MM-dd');

        if (selectedDateStr === originalDateStr) {
          const timeToMatch = bookingData.DateTimeInfo.SelectedTime;

          const matchingSlot = filteredSlots.find(slot => {
            try {
              const userSlotTime = TimezoneService.convertBackendTimeToLocal(slot.startTime);
              const slotTimeFormatted = format(userSlotTime, 'HH:mm');
              return slotTimeFormatted === timeToMatch;
            } catch (error) {
              console.error('Error comparing slot time:', error);
              return false;
            }
          });

          if (matchingSlot) {
            setSelectedSlot(matchingSlot);
          }
        }
      }

    } catch (error) {
      if (generation !== fetchTimeSlotsGenRef.current) return;
      console.error('❌ Error fetching time slots:', error);
      setTimeSlots([]);
      setSelectedSlot(null);
    } finally {
      if (generation === fetchTimeSlotsGenRef.current) {
        updateLoadingState('timeSlots', false);
      }
    }
  }, [selectedService, selectedDate, editMode, isViewMode, bookingData.DateTimeInfo?.SelectedTime, bookingData.ServiceId, dateRange, bookingData.DateTimeInfo?.FromDate, bookingData.DateTimeInfo?.ToDate, isOverrideMode]);

  const fetchAllFollowers = useCallback(async () => {
    updateLoadingState('followers', true);
    try {
      if(editMode === true && initialData?.followers !== null && initialData?.followers !== ""){
        setFetchedFollowers(initialData?.followers.split(','));
      } else {
        const [regularFollowers, globalFollowers] = await Promise.all([
          bookingService.getFollowers(),
          isAdmin ? bookingService.getGlobalFollowers() : Promise.resolve([])
        ]);
        
        const allSystemFollowers = Array.from(new Set([
          ...regularFollowers,
          ...globalFollowers
        ]));

        setFetchedFollowers(allSystemFollowers);
      }
    } catch (error) {
      console.error('Failed to fetch followers', error);
      setFetchedFollowers([]);
    } finally {
      updateLoadingState('followers', false);
    }
  }, [isAdmin, editMode, initialData]);

  const handleToggleLoanFollower = (follower: FollowerWithType) => {
  if (readOnlyMode) return;
  
  setSelectedLoanFollowers(prev => {
    const isSelected = prev.find(f => f.email === follower.email);
    if (isSelected) {
      // Remove from selected
      return prev.filter(f => f.email !== follower.email);
    } else {
      // Add to selected
      return [...prev, follower];
    }
  });
};

 const handleAddFollower = (newFollower: string) => {
    if (readOnlyMode) return;
    
    const cleanedFollower = newFollower.trim();
    
    if (!emailRegex.test(cleanedFollower)) {
      return;
    }
    
    const allExistingFollowers = [
      ...fetchedFollowers, 
      ...selectedLoanFollowers.map(slf => slf.email), 
      ...addedFollowers
    ];
    
    if (!allExistingFollowers.includes(cleanedFollower)) {
      setAddedFollowers(prev => [...prev, cleanedFollower]);
    }
  };

  useEffect(() => {
    if (prefilledLoanId && !editMode && !readOnlyMode && !bookingData.EncompassDetails.EncompassLoanId) {
      
      // Set the loan ID in the form
      setBookingData(prev => ({
        ...prev,
        EncompassDetails: {
          ...prev.EncompassDetails,
          EncompassLoanId: prefilledLoanId,
        },
      }));
      
      // Auto-fetch loan details with a small delay to ensure state is updated
      setTimeout(() => {
        fetchLoanDetails();
      }, 100);
    }
  }, [prefilledLoanId, editMode, readOnlyMode, bookingData.EncompassDetails.EncompassLoanId]);

useEffect(() => {
  const initializeEditMode = async () => {
    if (initialData) {
      updateLoadingState('initializing', true);
      setCustomLocationTouched(false);
              
      const formattedData = mapCalendarBookingToFormData(initialData as any);

      try {
        const loanDetails = await bookingService.getLoanDetails(bookingData.EncompassDetails.EncompassLoanId || initialData?.encompassLoanId);
        setLoanDetails(loanDetails);
        setShowLoanDetails(true);
        setIsLoanDetailsValidated(true);
        
        setNotes(initialData.loanData?.notes || loanDetails.notes || '');

        const [regularFollowers, globalFollowers, loanFollowersData] = await Promise.all([
          bookingService.getFollowers(), 
          bookingService.getGlobalFollowers(), 
          loanDetails.followers && Array.isArray(loanDetails.followers) && loanDetails.followers.length > 0
            ? Promise.resolve(parseFollowersWithTypes(loanDetails.followers))
            : Promise.resolve([])
        ]);

        const allSystemFollowers = Array.from(new Set([
          ...regularFollowers,
          ...globalFollowers
        ]));
        
        let borrowerEmailsFromLoan: string[] = [];
        if (loanDetails.borrowers && Array.isArray(loanDetails.borrowers)) {
          loanDetails.borrowers.forEach(borrowerGroup => {
            if (borrowerGroup.borrower?.email) {
              borrowerEmailsFromLoan.push(borrowerGroup.borrower.email);
            }
            if (borrowerGroup.coBorrower?.email) {
              borrowerEmailsFromLoan.push(borrowerGroup.coBorrower.email);
            }
          });
        }
        
        if(initialData.followers) {
          const existingFollowerEmails = initialData.followers
            .split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0);

          const systemFollowersInBooking = existingFollowerEmails.filter(email => 
            allSystemFollowers.includes(email)
          );
          
          const loanFollowersInBooking = loanFollowersData.filter(lf => 
            existingFollowerEmails.includes(lf.email)
          );
          
          const borrowerEmailsInBooking = existingFollowerEmails.filter(email =>
            borrowerEmailsFromLoan.includes(email)
          );
          
          const customFollowers = existingFollowerEmails.filter(email => 
            !allSystemFollowers.includes(email) && 
            !loanFollowersData.find(lf => lf.email === email) &&
            !borrowerEmailsFromLoan.includes(email) 
          );
          
          setLoanDetailsFollowers(loanFollowersData);
          setSelectedLoanFollowers(loanFollowersInBooking);
          setFetchedFollowers(systemFollowersInBooking);
          setSelectedBorrowerEmails(borrowerEmailsInBooking);
          setAddedFollowers(customFollowers);
          
        } else {
          setLoanDetailsFollowers(loanFollowersData);
          setSelectedLoanFollowers([]);
          setFetchedFollowers([]);
          setSelectedBorrowerEmails(borrowerEmailsFromLoan);
          setAddedFollowers([]);
        }
        
        setBookingData((prev) => ({
          ...formattedData,
          BorrowerInformation: {
            FirstName: loanDetails.borrowerFirstName,
            LastName: loanDetails.borrowerLastName,
            Email: loanDetails.borrowerEmail,
            PhoneNumber: loanDetails.borrowerPhone,
            Address: {
              Street: loanDetails.borrowerAddress,
              City: loanDetails.borrowerCity,
              State: loanDetails.borrowerState,
              ZipCode: loanDetails.borrowerZipCode,
            },
          },
          EncompassDetails: {
            EncompassLoanId: loanDetails?.loanId,
            EncompassLoanNumber: loanDetails?.loanNumber,
            LoanCloser: loanDetails?.loanCloser,
            loanCloserEmail: loanDetails?.loanCloserEmail,
            LoanOfficer: loanDetails?.loanOfficer,
            loanOfficerEmail: loanDetails?.loanOfficerEmail,
            dpa: loanDetails?.dpa
          },
          CustomLocation: formattedData.CustomLocation || '',
        }));

        // Set the selected date from formatted data
        if (formattedData.DateTimeInfo?.SelectedDate) {
          const dateStr = formattedData.DateTimeInfo.SelectedDate;            
          if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-').map(Number);
            const correctDate = new Date(year, month - 1, day);
            setSelectedDate(correctDate);
            setInitialSelectedDate(correctDate);
          } else {
            setSelectedDate(new Date());
          }
        }

      } catch (error) {
        console.error('Error initializing edit mode:', error);
        
        // On error, still try to set basic followers if available
        if (initialData.followers) {
          const followerArray = initialData.followers
            .split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0);
          
          setFetchedFollowers(followerArray);
          setSelectedLoanFollowers([]);
          setAddedFollowers([]);
        }
      } finally {
        updateLoadingState('initializing', false);
      }
    }
  };

  initializeEditMode();
}, [editMode, initialData]);

// Fetch availability settings in edit mode when service is known
useEffect(() => {
  if (editMode && !readOnlyMode && !isOverrideMode && bookingData.ServiceId) {
    fetchAvailabilitySettings(bookingData.ServiceId);
  }
}, [editMode, readOnlyMode, isOverrideMode, bookingData.ServiceId, fetchAvailabilitySettings]);

 useEffect(() => {
    fetchAvailableServicesData();
    fetchAllFollowers(); 
    
  }, [fetchAvailableServicesData, fetchAllFollowers]);

  useEffect(() => {
    if (!editMode && loanDetails) {
      fetchAllFollowers();
    }
  }, [loanDetails, editMode, fetchAllFollowers]);

useEffect(() => {
    const allFollowers = [
      ...fetchedFollowers,
      ...selectedLoanFollowers.map(slf => slf.email), 
      ...selectedBorrowerEmails,                            
      ...addedFollowers 
    ];
    
    const uniqueFollowers = Array.from(new Set(allFollowers));
    const followersString = uniqueFollowers.join(',');
    
    
    setBookingData(prev => ({
      ...prev,
      Followers: followersString,
    }));
    
  }, [fetchedFollowers, selectedLoanFollowers, selectedBorrowerEmails, addedFollowers]);

  useEffect(() => {
    if (selectedDate && bookingData.ServiceId && !isOverrideMode) {
      fetchAvailableTimeSlots();
    }
  }, [selectedDate, bookingData.ServiceId, isOverrideMode]);

  useEffect(() => {
    if (!readOnlyMode && (selectedSlot || (isOverrideMode && overrideSlot))) {
      handleDateTimeSelect();
    }
  }, [selectedSlot, isOverrideMode, overrideSlot]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          backdropFilter: 'blur(3px)',
        }}
        open={loadingStates.submitting || loadingStates.initializing}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">
            {loadingStates.submitting ? (editMode ? 'Updating booking...' : 'Creating booking...') : 
            loadingStates.initializing ? 'Loading booking details...' : 
            loadingStates.sendingEmail ? 'Sending email notifications...' : 'Processing...'}
          </Typography>
        </Box>
      </Backdrop>

      {isAnyLoading && !loadingStates.submitting && !loadingStates.initializing && (
        <Box sx={{ width: '100%', position: 'sticky', top: 0, zIndex: 10 }}>
          <LinearProgress />
        </Box>
      )}

      <Grid
      container 
      spacing={2} 
      padding={2}
      sx={{
        width: '100%',
        margin: 0,
        borderRadius: '10px',
        color: 'gray',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        position: 'relative',
      }}
      >
        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Typography sx={{fontSize: '30px', fontWeight: 'bold', color: 'black'}}>
              {getFormTitle()}
            </Typography>
            <CloseIcon sx={{float: 'right', color: 'gray', cursor: 'pointer'}} onClick={onClose} />
        </Grid>

        <Box sx={{ width: '100%', padding: 2 }}>
          <Grid item xs={12}>
            <Typography variant="h6">Client Details</Typography>
            <Grid sx={{display: 'flex', gap: '4px', mt: '10px', maxHeight: '56px'}}>
              <TextField
                fullWidth
                label="Encompass Loan ID"
                variant='outlined'
                required
                value={bookingData.EncompassDetails.EncompassLoanId || ''}
                onChange={handleLoanIdChange}
                onKeyDown={!editMode && !readOnlyMode ? handleLoanIdKeyDown : undefined}
                InputProps={{
                  readOnly: editMode || readOnlyMode,
                }}
                helperText={
                  prefilledLoanId && !editMode && !readOnlyMode
                    ? "Press Enter after entering Loan ID" 
                    : !editMode && !readOnlyMode 
                      ? "Press Enter after entering Loan ID" 
                      : ""
                }
              />
              {!editMode && !readOnlyMode && (
                <Button 
                  disabled={!bookingData.EncompassDetails.EncompassLoanId || loadingStates.loanDetails} 
                  variant="contained" 
                  onClick={() => fetchLoanDetails()}
                  sx={{ minWidth: 120 }}
                >
                  {loadingStates.loanDetails ? <CircularProgress size={24} color="inherit" /> : 'Enter'}
                </Button>
              )}
             
            </Grid>
             {error.length > 0 && !readOnlyMode && (
              <Typography color="error" variant="caption" sx={{ margin: 2, display: 'block' }}>
                {error}
              </Typography>
              )}
          </Grid>
        </Box>

        {loadingStates.loanDetails && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Loading loan details...</Typography>
            </Box>
          </Grid>
        )}

        {showLoanDetails && (
        <Box sx={{ width: '100%', padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Borrower Information</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                variant='outlined'
                value={bookingData.BorrowerInformation.FirstName}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                variant='outlined'
                value={bookingData.BorrowerInformation.LastName || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                variant='outlined'
                value={bookingData.BorrowerInformation.Email || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone Number"
                variant='outlined'
                value={bookingData.BorrowerInformation.PhoneNumber || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                variant='outlined'
                value={bookingData.BorrowerInformation.Address.Street || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="City"
                variant='outlined'
                value={bookingData.BorrowerInformation.Address.City || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
              fullWidth
              label="State"
              variant='outlined'
              value={bookingData.BorrowerInformation.Address.State || ''}
              InputProps={{
                  readOnly: true,
                }}/>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Zip Code"
                variant='outlined'
                value={bookingData.BorrowerInformation.Address.ZipCode || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Loan Details</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Loan Number"
                variant='outlined'
                value={loanDetails?.loanNumber || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Loan Type"
                variant="outlined"    
                value={loanDetails?.loanType || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Loan Amount"
                variant='outlined'
                InputProps={{
                  readOnly: true,
                }}
                value={formatLoanAmount(loanDetails?.loanAmount)}/>
            </Grid>
            <Grid item xs={6}>
            <TextField
              fullWidth
              value={loanDetails?.loanCloser || ''}
              label="Loan Closer"
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"/>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Loan Purpose"
                variant="outlined"    
                value={loanDetails?.loanPurpose || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="DPA Program"
                InputProps={{
                  readOnly: true,
                }}
                variant='outlined'
                value={loanDetails?.dpa || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                variant="outlined"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                InputProps={{
                  readOnly: readOnlyMode || editMode,
                }}
                placeholder={readOnlyMode ? "No notes available" : "Add any additional notes for this appointment..."}
                helperText={readOnlyMode ? "" : "Optional notes that will be saved with this appointment"}
              />
            </Grid>
          </Grid>
        </Box>
      )}

        <Grid item xs={12} sx={{padding: '16px'}}>
          <Typography variant="h6">Appointment Details</Typography>
          <TextField
            fullWidth
            value={bookingData.ServiceName || ''}
            required
            onChange={handleServiceChange}
            select={!editMode && !readOnlyMode}
            label="Location"
            variant="outlined"    
            InputProps={{
              readOnly: editMode || readOnlyMode,
              endAdornment: loadingStates.services ? <CircularProgress size={20} /> : null,
            }}
            sx={{mt: '10px'}}
          >
          {!readOnlyMode && (Array.isArray(services) ? services : []).map((service) => (
            <MenuItem key={service.id} value={service.displayName}>
              {service.displayName}
            </MenuItem>
          ))}
          </TextField>
          {!readOnlyMode && (
            <TextField
              fullWidth
              label="Custom Location (overrides location address)"
              value={bookingData.CustomLocation || ''}
              onChange={(e) => {
                setCustomLocationTouched(true);
                setBookingData(prev => ({ ...prev, CustomLocation: e.target.value }));
              }}
              variant="outlined"
              sx={{ mt: '10px' }}
              placeholder="Leave blank to use the default address for the selected location"
              helperText="Optional. If provided, this address appears in the confirmation email instead of the default."
            />
          )}
          {readOnlyMode && bookingData.CustomLocation && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Custom Location:</strong> {bookingData.CustomLocation}
            </Typography>
          )}
        </Grid>

        {isLoadingAvailability && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Loading availability settings...</Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sx={{padding: '16px'}}>
          <StaticDatePicker
            disabled={(!selectedService && !editMode) || readOnlyMode || isLoadingAvailability}
            value={selectedDate}
            onChange={(date: Date | null) => !readOnlyMode && setSelectedDate(date)}
            orientation={isMobile ? 'portrait' : 'landscape'}
            slotProps={{
              actionBar: { actions: [] }
            }}
            readOnly={readOnlyMode}
            minDate={(() => {
              if (readOnlyMode || isOverrideMode) return undefined;
              if (editMode) {
                // In edit mode, only prevent past dates (today is the minimum)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return today;
              }
              // In create mode, use the calculated minDate (includes lead time)
              return dateRange?.minDate;
            })()}
            maxDate={!readOnlyMode && dateRange && !isOverrideMode && !editMode ? dateRange.maxDate : undefined}
            shouldDisableDate={(date) => {
              if (!editMode && !readOnlyMode && !isOverrideMode) {
                // Create mode: apply full availability rules (lead time + max advance)
                if (!selectedService) {
                  return true;
                }

                if (dateRange) {
                  const shouldDisable = AvailabilityService.shouldDisableDate(date, dateRange);
                  if (shouldDisable) {
                    return true;
                  }
                }

                return false;
              }

              if (editMode) {
                // Edit mode: Only disable past dates, no other restrictions
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const dateToCheck = new Date(date);
                dateToCheck.setHours(0, 0, 0, 0);

                // Disable dates in the past only
                if (dateToCheck < today) {
                  return true;
                }

                // Allow all future dates (no max advance restriction in edit mode)
                return false;
              }

              // Default: don't disable (for override mode and other cases)
              return false;
            }}
            // Keep disablePast for create mode only (not override mode)
            disablePast={!editMode && !readOnlyMode && !isOverrideMode}
          />
          
          {/* Show message when no service is selected */}
          {!editMode && !readOnlyMode && !selectedService && !isOverrideMode && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="grey.700">
                Please select a service first to see available dates.
              </Typography>
            </Box>
          )}
          
          {/* Show message when service is selected but availability is loading */}
          {!editMode && !readOnlyMode && selectedService && isLoadingAvailability && !isOverrideMode && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="grey.700">
                Loading availability for selected service...
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Admin Override Section */}
        {!readOnlyMode && (
          <Grid item xs={12} sx={{ padding: '16px' }}>
            <AdminOverride
              isAdmin={isAdmin}
              isEditMode={editMode}
              selectedDate={selectedDate}
              onOverrideSlot={handleOverrideSlot}
              onCancelOverride={handleCancelOverride}
              disabled={loadingStates.submitting || loadingStates.initializing}
            />
          </Grid>
        )}

        {/* Show override confirmation when in override mode */}
        {isOverrideMode && overrideSlot && (
          <Grid item xs={12} sx={{ padding: '16px' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Admin Override Active
              </Typography>
              <Typography variant="body2">
                Custom time slot: {overrideSlot.displayText}
              </Typography>
              <Button
                size="small"
                onClick={handleCancelOverride}
                sx={{ mt: 1 }}
                variant="outlined"
                color="warning"
              >
                Cancel Override
              </Button>
            </Alert>
          </Grid>
        )}


      {/* Time Display/Selector Section */}
      <Grid container sx={{display: 'flex', flexDirection: 'column', width: '100%', padding: '16px'}}>
        {/* For view mode (read-only), just show the time as text */}
        {readOnlyMode ? (
          <Box sx={{ justifySelf: 'start', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Appointment Time
            </Typography>
            
            {initialData?.start?.dateTime && initialData?.end?.dateTime ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  display: 'inline-block',
                  fontWeight: 'medium'
                }}>
                  📅 {TimezoneService.formatDateForUser(initialData.start.dateTime, 'EEEE, MMMM d, yyyy')}
                  <br />
                  🕐 {TimezoneService.formatTimeForUser(initialData.start.dateTime, 'h:mm a')} - {TimezoneService.formatTimeForUser(initialData.end.dateTime, 'h:mm a')}
                  <br />
                  🌍 {TimezoneService.getUserTimezoneDisplay()}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No time information available
              </Typography>
            )}
          </Box>
        ) : (
          /* Regular TimeSelector for edit mode (upcoming only) or create mode */
          (timeSlots.length > 0 || selectedSlot || (editMode && selectedDate)) && !isOverrideMode && (
            <TimeSelector
              timeSlots={timeSlots}
              selectedSlot={selectedSlot}
              onSelect={setSelectedSlot}
              loading={loadingStates.timeSlots}
              readOnly={false}
              editMode={editMode}
              originalSlot={editMode && initialData ? {
                startTime: initialData.start?.dateTime || '',
                endTime: initialData.end?.dateTime || '',
                displayText: '',
                staffMemberId: initialData.staffMemberIds?.[0] || ''
              } : null}
            />
          )
        )}
        
        <EnhancedFollowers
            editMode={!readOnlyMode}
            regularFollowers={fetchedFollowers}              
            loanDetailsFollowers={loanDetailsFollowers}      
            selectedLoanFollowers={selectedLoanFollowers}    
            addedFollowers={addedFollowers}                  
            borrowers={loanDetails?.borrowers || []} 
            selectedBorrowerEmails={selectedBorrowerEmails}
            onToggleLoanFollower={handleToggleLoanFollower}  
            onAddFollower={handleAddFollower}                
            onRemoveAddedFollower={(followerToRemove) => {   
              if (!readOnlyMode) {
                setAddedFollowers(prev => prev.filter(f => f !== followerToRemove));
              }
            }}
            onBorrowerSelectionChange={handleBorrowerSelectionChange}
            loading={loadingStates.followers}
          />
        
        {error.length > 0 && !readOnlyMode && (
          <Typography color="error" variant="caption" sx={{ margin: 2, display: 'block' }}>
            {error}
          </Typography>
        )}

        {/* Buttons section remains the same */}
        {!readOnlyMode && (
          <Grid sx={{display: 'flex', gap: '1rem'}}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={onClose}
                disabled={loadingStates.submitting}
                sx={{
                  backgroundColor: '#D3323A',
                }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isAnyLoading || (!editMode && !isLoanDetailsValidated && !isOverrideMode)}
                onMouseDown={(e) => e.preventDefault()}
                sx={{
                  position: 'relative',
                }}
              >
                {loadingStates.submitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    {editMode ? 'Updating...' : 'Scheduling...'}
                  </Box>
                ) : (
                  editMode ? 'Update' : 'Schedule'
                )}
              </Button>
            </Grid>
          </Grid>
        )}

        {readOnlyMode && (
          <Grid sx={{display: 'flex', justifyContent: 'center', mt: '16px'}}>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                minWidth: 150,
              }}
            >
              Close
            </Button>
          </Grid>
        )}
      </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default CreateBookingForm;


function getPhysicalAddressForService(ServiceName: string) {
  switch(ServiceName) {
    case "FEMBi Mortgage - San Juan":
      return "322 Ave De Diego Esq. Roosevelt Suite 201, San Juan, PR, 00920";
    case "FEMBi Mortgage - Ponce":
      return "San Rafael Industrial Park 1634 Ste 201, Ponce, PR, 00716";
  }
}
